import React, { useRef } from 'react';
import type { TestimonialsData } from 'types/websiteBuilder';
import { compressImageFile } from 'utils/imageUtils';
import avatarPlaceholder from 'assets/websiteBuilderDefaults/avatar-placeholder.svg';
import styles from './TestimonialsSection.module.scss';

interface TestimonialsSectionEditorProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function getData(data: Record<string, unknown>): TestimonialsData {
  const items = Array.isArray(data.items)
    ? (data.items as Record<string, unknown>[]).map((i) => ({
        quote: typeof i.quote === 'string' ? i.quote : '',
        authorName: typeof i.authorName === 'string' ? i.authorName : '',
        authorTitle: typeof i.authorTitle === 'string' ? i.authorTitle : '',
        imageUrl: typeof i.imageUrl === 'string' ? i.imageUrl : '',
      }))
    : [];
  return {
    title: typeof data.title === 'string' ? data.title : 'What People Say',
    items:
      items.length > 0
        ? items
        : [{ quote: '', authorName: '', authorTitle: '', imageUrl: '' }],
  };
}

export function TestimonialsSectionEditor({
  data,
  onUpdateData,
}: TestimonialsSectionEditorProps) {
  const d = getData(data);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadIndexRef = useRef<number>(0);

  const updateItem = (index: number, field: string, value: string) => {
    const next = [...(d.items ?? [])];
    if (!next[index])
      next[index] = {
        quote: '',
        authorName: '',
        authorTitle: '',
        imageUrl: '',
      };
    (next[index] as Record<string, string>)[field] = value;
    onUpdateData({ ...data, items: next });
  };

  const addItem = () => {
    onUpdateData({
      ...data,
      items: [
        ...(d.items ?? []),
        {
          quote: '',
          authorName: '',
          authorTitle: '',
          imageUrl: avatarPlaceholder,
        },
      ],
    });
  };

  const removeItem = (index: number) => {
    const next = (d.items ?? []).filter((_, i) => i !== index);
    onUpdateData({ ...data, items: next });
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file == null) return;
    const index = uploadIndexRef.current;
    compressImageFile(file)
      .then((imageUrl) => updateItem(index, 'imageUrl', imageUrl))
      .catch(() => {});
    e.target.value = '';
  };

  const triggerImageUpload = (index: number) => {
    uploadIndexRef.current = index;
    fileInputRef.current?.click();
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className={styles.fileInput}
        onChange={handleImageFileChange}
        aria-label="Upload avatar image"
      />
      <input
        type="text"
        className={styles.titleInput}
        value={d.title}
        onChange={(e) => onUpdateData({ ...data, title: e.target.value })}
        placeholder="Section title"
      />
      <div className={styles.list}>
        {(d.items ?? []).map((item, i) => (
          <div key={i} className={styles.card}>
            <textarea
              className={styles.quoteInput}
              value={item.quote}
              onChange={(e) => updateItem(i, 'quote', e.target.value)}
              placeholder="Quote"
              rows={2}
            />
            <input
              type="text"
              value={item.authorName}
              onChange={(e) => updateItem(i, 'authorName', e.target.value)}
              placeholder="Author name"
            />
            <input
              type="text"
              value={item.authorTitle}
              onChange={(e) => updateItem(i, 'authorTitle', e.target.value)}
              placeholder="Author title"
            />
            <div className={styles.imageUrlRow}>
              <input
                type="text"
                value={item.imageUrl}
                onChange={(e) => updateItem(i, 'imageUrl', e.target.value)}
                placeholder="Image URL"
              />
              <button
                type="button"
                className={styles.browseBtn}
                onClick={() => triggerImageUpload(i)}
              >
                Browse
              </button>
              <button
                type="button"
                className={styles.clearBtn}
                onClick={() => updateItem(i, 'imageUrl', '')}
              >
                Clear
              </button>
            </div>
            <button
              type="button"
              className={styles.removeBtn}
              onClick={() => removeItem(i)}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
      <button type="button" className={styles.addBtn} onClick={addItem}>
        + Add testimonial
      </button>
    </>
  );
}

interface TestimonialsSectionProps {
  data: Record<string, unknown>;
  onUpdateData: (data: Record<string, unknown>) => void;
}

function TestimonialsSection({ data }: TestimonialsSectionProps) {
  const d = getData(data);

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <h2 className={styles.title}>{d.title}</h2>
        <div className={styles.list}>
          {(d.items ?? []).map((item, i) => (
            <div key={i} className={styles.card}>
              <blockquote className={styles.quote}>{item.quote}</blockquote>
              <div className={styles.author}>
                <img
                  src={item.imageUrl || avatarPlaceholder}
                  alt=""
                  className={styles.avatar}
                />
                <div>
                  <strong>{item.authorName}</strong>
                  {item.authorTitle && (
                    <span className={styles.authorTitle}>
                      {' '}
                      — {item.authorTitle}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default TestimonialsSection;
